import React, { useState, useEffect } from 'react';
import useWindowWidth from '../hooks/useWindowWidth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '14px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
    paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
  },
  simGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px', marginBottom: '16px',
  },
  simCard: {
    padding: '12px', borderRadius: '8px', border: '2px solid #e5e7eb',
    cursor: 'pointer', userSelect: 'none',
  },
  simCardSelected: { border: '2px solid #0052CC', backgroundColor: '#EBF5FF' },
  simTitle: { fontSize: '13px', fontWeight: '700', color: '#111827', marginBottom: '4px' },
  simMeta: { fontSize: '11px', color: '#6b7280' },
  simScore: { fontSize: '16px', fontWeight: '800', color: '#0052CC', marginTop: '4px' },
  compareBtn: {
    backgroundColor: '#0052CC', color: '#fff', border: 'none',
    borderRadius: '8px', padding: '10px 24px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer',
  },
  compareBtnDisabled: {
    backgroundColor: '#d1d5db', color: '#9ca3af', border: 'none',
    borderRadius: '8px', padding: '10px 24px', fontSize: '14px',
    fontWeight: '600', cursor: 'not-allowed',
  },
  comparisonGrid: {
    display: 'grid',
    gap: '2px',
    overflowX: 'auto',
  },
  compRow: {
    display: 'grid',
    gap: '1px',
    alignItems: 'center',
  },
  compLabel: {
    fontSize: '12px', fontWeight: '700', color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    padding: '10px 16px', backgroundColor: '#f9fafb',
    borderRadius: '6px',
  },
  compCell: {
    padding: '10px 16px', backgroundColor: '#fff',
    border: '1px solid #e5e7eb', borderRadius: '6px',
    fontSize: '13px', color: '#374151',
  },
  compCellHighlight: {
    padding: '10px 16px', backgroundColor: '#EBF5FF',
    border: '1px solid #bfdbfe', borderRadius: '6px',
    fontSize: '13px', fontWeight: '700', color: '#0052CC',
  },
  scoreBar: {
    width: '100%', height: '6px', backgroundColor: '#e5e7eb',
    borderRadius: '3px', overflow: 'hidden', marginTop: '4px',
  },
  scoreBarFill: { height: '100%', borderRadius: '3px' },
  metricRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '8px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  metricLabel: { fontSize: '13px', color: '#374151', fontWeight: '500' },
  metricScores: { display: 'flex', gap: '8px' },
  metricBadge: {
    fontSize: '12px', fontWeight: '700', padding: '2px 8px',
    borderRadius: '20px', minWidth: '45px', textAlign: 'center',
  },
  exportBtn: {
    backgroundColor: '#f3f4f6', color: '#374151', border: 'none',
    borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  loading: { textAlign: 'center', padding: '32px', color: '#6b7280' },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
    borderRadius: '8px', padding: '12px 16px', fontSize: '13px', marginBottom: '16px',
  },
  hint: { fontSize: '12px', color: '#9ca3af', marginTop: '8px' },
  winnerBadge: {
    fontSize: '10px', fontWeight: '700', color: '#16a34a',
    backgroundColor: '#dcfce7', padding: '2px 6px', borderRadius: '20px',
    marginLeft: '6px',
  },
};

function getScoreColor(score) {
  if (score === null || score === undefined) return '#9ca3af';
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#0052CC';
  if (score >= 40) return '#d97706';
  return '#dc2626';
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function groupByMonthYear(items) {
  const groups = {};
  items.forEach(item => {
    const date = new Date(item.created_at);
    const year = date.getFullYear();
    const month = date.getMonth();
    const key = `${year}-${month}`;
    if (!groups[key]) groups[key] = { year, month, items: [] };
    groups[key].items.push(item);
  });
  return Object.values(groups).sort((a, b) =>
    b.year !== a.year ? b.year - a.year : b.month - a.month
  );
}

export default function Benchmark() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [selected, setSelected] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState('');
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => { fetchSimulations(); }, []);

  async function fetchSimulations() {
    try {
      setLoadingList(true);
      const res = await fetch(`${API}/api/analytics/history?owner_id=${currentUser.uid}`);
      const data = await res.json();
      // Only show completed simulations with evaluations
      const completed = Array.isArray(data)
        ? data.filter(s => s.status === 'completed' && s.overall_score !== null)
        : [];
      setSimulations(completed);
    } catch {
      setError('Failed to load simulations.');
    } finally {
      setLoadingList(false);
    }
  }

  function toggleSelect(simId) {
    setSelected(prev =>
      prev.includes(simId) ? prev.filter(id => id !== simId) : [...prev, simId]
    );
    setComparison(null);
  }

  async function runComparison() {
    if (selected.length < 2) return;
    setLoading(true);
    setError('');
    setComparison(null);
    try {
      const res = await fetch(
        `${API}/api/analytics/benchmark?simulation_ids=${selected.join(',')}`
      );
      if (!res.ok) throw new Error('Benchmark failed');
      const data = await res.json();
      setComparison(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function exportComparison() {
    if (!comparison) return;
    const lines = [
      'V2VLab Benchmark Report',
      `Generated: ${new Date().toLocaleString()}`,
      `Simulations compared: ${comparison.length}`,
      '',
      '─'.repeat(60),
      '',
      ...comparison.map((sim, idx) => [
        `Run ${idx + 1}: ${sim.scenario_title}`,
        `Provider: ${sim.llm_provider} / ${sim.llm_model}`,
        `Output Mode: ${sim.output_mode}`,
        `Overall Score: ${sim.overall_score}/100`,
        `Total Turns: ${sim.total_turns}`,
        `Total Words: ${sim.total_words}`,
        sim.stt_accuracy !== null ? `STT Accuracy: ${sim.stt_accuracy}%` : '',
        '',
        'Metric Scores:',
        ...Object.entries(sim.scores).map(([metric, data]) =>
          `  ${metric}: ${data.score}/100`
        ),
        '',
      ]).flat(),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `v2vlab_benchmark_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Find all unique metrics across all compared simulations
  const allMetrics = comparison
    ? [...new Set(comparison.flatMap(s => Object.keys(s.scores)))]
    : [];

  // Find winner for each metric
  function getWinner(metric) {
    if (!comparison) return null;
    const scores = comparison.map(s => s.scores[metric]?.score ?? 0);
    const max = Math.max(...scores);
    return scores.indexOf(max);
  }

  const overallWinner = comparison
    ? comparison.indexOf(comparison.reduce((best, s) =>
        (s.overall_score ?? -1) > (best.overall_score ?? -1) ? s : best, comparison[0]))
    : -1;

  return (
    <AppLayout>
      <div style={{ ...styles.header, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '12px' : '0' }}>
        <div>
          <div style={styles.title}>Benchmark</div>
          <div style={styles.sub}>Compare simulation runs side by side</div>
        </div>
        {comparison && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button style={styles.exportBtn} onClick={exportComparison}>
              ⬇ Export TXT
            </button>
            <button
              style={{ ...styles.exportBtn, backgroundColor: '#0052CC', color: '#fff', border: 'none' }}
              onClick={() => window.open(
                `${API}/api/reports/comparison?owner_id=${currentUser.uid}&simulation_ids=${selected.join(',')}`,
                '_blank'
              )}
            >
              ⬇ PDF Report
            </button>
          </div>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Simulation Selection */}
      <div style={styles.card}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '12px' : '0',
          marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
        }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#0052CC', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Select Simulations to Compare ({selected.length} selected)
          </div>
          <div style={{
            display: 'flex', alignItems: isMobile ? 'stretch' : 'center',
            flexDirection: 'column', gap: '4px', width: isMobile ? '100%' : 'auto',
          }}>  <button
              style={{
                ...(selected.length >= 2 ? styles.compareBtn : styles.compareBtnDisabled),
                width: isMobile ? '100%' : 'auto',
              }}
              onClick={runComparison}
              disabled={selected.length < 2 || loading}
            >
              {loading ? 'Comparing...' : `Compare ${selected.length} Runs`}
            </button>
            {selected.length < 2 && (
              <div style={{ ...styles.hint, textAlign: 'center' }}>Select at least 2</div>
            )}
          </div>
        </div>
        #START
        {loadingList ? (
          <div style={styles.loading}>Loading simulations...</div>
        ) : simulations.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            No completed simulations with evaluations yet.{' '}
            <span
              style={{ color: '#0052CC', cursor: 'pointer', fontWeight: '600' }}
              onClick={() => navigate('/scenarios')}
            >
              Run a simulation →
            </span>
          </div>
        ) : (
          <>
            {groupByMonthYear(simulations).map(group => {
              const key = `${group.year}-${group.month}`;
              const isOpen = openGroups[key] === true;
              const selectedInGroup = group.items.filter(s => selected.includes(s.id)).length;
              return (
                <div key={key} style={{ marginBottom: '12px' }}>
                  <div
                    onClick={() => setOpenGroups(prev => ({ ...prev, [key]: !isOpen }))}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', backgroundColor: '#f9fafb', borderRadius: '8px',
                      cursor: 'pointer', marginBottom: isOpen ? '10px' : '0',
                    }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>
                      {MONTH_NAMES[group.month]} {group.year} ({group.items.length})
                      {selectedInGroup > 0 && (
                        <span style={{ color: '#0052CC', marginLeft: '8px' }}>· {selectedInGroup} selected</span>
                      )}
                    </span>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{isOpen ? '▲' : '▼'}</span>
                  </div>

                  {isOpen && (
                    <div style={styles.simGrid}>
                      {group.items.map(sim => {
                        const isSelected = selected.includes(sim.id);
                        return (
                          <div
                            key={sim.id}
                            style={{ ...styles.simCard, ...(isSelected ? styles.simCardSelected : {}) }}
                            onClick={() => toggleSelect(sim.id)}
                          >
                            <div style={styles.simTitle}>{sim.scenario_title}</div>
                            <div style={styles.simMeta}>
                              {sim.llm_provider} · {sim.llm_model}
                            </div>
                            <div style={styles.simMeta}>
                              {new Date(sim.created_at).toLocaleDateString()} · {sim.current_turn} turns
                            </div>
                            {sim.overall_score !== null && (
                              <div style={{
                                ...styles.simScore,
                                color: getScoreColor(sim.overall_score),
                              }}>
                                {sim.overall_score}/100
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Comparison Results */}
      {comparison && (
        <>
          {/* Overview */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Overview</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : `200px ${comparison.map(() => '1fr').join(' ')}`,
              overflowX: isMobile ? 'auto' : 'visible',
              gap: '8px',
            }}>
              {/* Headers */}
              <div style={styles.compLabel}>Run</div>
              {comparison.map((sim, idx) => (
                <div key={idx} style={styles.compCell}>
                  <div style={{ fontWeight: '700', color: '#111827' }}>
                    Run {idx + 1}
                    {idx === overallWinner && (
                      <span style={styles.winnerBadge}>★ Best</span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                    {sim.scenario_title}
                  </div>
                </div>
              ))}

              {/* Provider */}
              <div style={styles.compLabel}>Provider</div>
              {comparison.map((sim, idx) => (
                <div key={idx} style={styles.compCell}>
                  {sim.llm_provider}
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{sim.llm_model}</div>
                </div>
              ))}

              {/* Output Mode */}
              <div style={styles.compLabel}>Modality</div>
              {comparison.map((sim, idx) => (
                <div key={idx} style={styles.compCell}>{sim.output_mode}</div>
              ))}

              {/* Overall Score */}
              <div style={styles.compLabel}>Overall Score</div>
              {comparison.map((sim, idx) => (
                <div key={idx} style={
                  idx === overallWinner ? styles.compCellHighlight : styles.compCell
                }>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: getScoreColor(sim.overall_score) }}>
                    {sim.overall_score ?? '—'}/100
                  </div>
                  <div style={styles.scoreBar}>
                    <div style={{
                      ...styles.scoreBarFill,
                      width: `${sim.overall_score || 0}%`,
                      backgroundColor: getScoreColor(sim.overall_score),
                    }} />
                  </div>
                </div>
              ))}

              {/* Turns */}
              <div style={styles.compLabel}>Turns</div>
              {comparison.map((sim, idx) => (
                <div key={idx} style={styles.compCell}>{sim.total_turns}</div>
              ))}

              {/* Words */}
              <div style={styles.compLabel}>Total Words</div>
              {comparison.map((sim, idx) => (
                <div key={idx} style={styles.compCell}>{sim.total_words}</div>
              ))}

              {/* Avg Words/Turn */}
              <div style={styles.compLabel}>Avg Words/Turn</div>
              {comparison.map((sim, idx) => (
                <div key={idx} style={styles.compCell}>{sim.avg_words_per_turn}</div>
              ))}

              {/* STT Accuracy — only show if any run has it */}
              {comparison.some(s => s.stt_accuracy !== null) && (
                <>
                  <div style={styles.compLabel}>STT Accuracy</div>
                  {comparison.map((sim, idx) => (
                    <div key={idx} style={styles.compCell}>
                      {sim.stt_accuracy !== null ? `${sim.stt_accuracy}%` : '—'}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Per-Metric Scores */}
          {allMetrics.length > 0 && (
            <div style={styles.card}>
              <div style={styles.sectionTitle}>Metric Breakdown</div>
              {allMetrics.map(metric => {
                const winnerIdx = getWinner(metric);
                return (
                  <div key={metric} style={styles.metricRow}>
                    <div style={styles.metricLabel}>
                      {metric}
                      {winnerIdx >= 0 && (
                        <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '8px' }}>
                          Run {winnerIdx + 1} leads
                        </span>
                      )}
                    </div>
                    <div style={styles.metricScores}>
                      {comparison.map((sim, idx) => {
                        const score = sim.scores[metric]?.score ?? null;
                        const isWinner = idx === winnerIdx;
                        return (
                          <div
                            key={idx}
                            style={{
                              ...styles.metricBadge,
                              backgroundColor: score !== null
                                ? `${getScoreColor(score)}20`
                                : '#f3f4f6',
                              color: score !== null ? getScoreColor(score) : '#9ca3af',
                              border: isWinner ? `2px solid ${getScoreColor(score)}` : '2px solid transparent',
                            }}
                          >
                            {score !== null ? `${score}` : '—'}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px' }}>
                {comparison.map((_, idx) => `Run ${idx + 1}`).join(' · ')}
              </div>
            </div>
          )}

          {/* Summaries */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Evaluation Summaries</div>
            {comparison.map((sim, idx) => (
              sim.summary && (
                <div key={idx} style={{
                  marginBottom: '16px', padding: '12px 16px',
                  backgroundColor: '#f9fafb', borderRadius: '8px',
                  borderLeft: '3px solid #0052CC',
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#0052CC', marginBottom: '6px' }}>
                    Run {idx + 1} — {sim.scenario_title}
                    {idx === overallWinner && <span style={styles.winnerBadge}>★ Best Overall</span>}
                  </div>
                  <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
                    {sim.summary}
                  </div>
                </div>
              )
            ))}
          </div>
        </>
      )}
    </AppLayout>
  );
}
